import { DeleteButton } from '@/components/Elements/Button'
import {
  InputCell,
  NumberInputCell,
  PriceCell,
  RawTextCell,
  SelectCell,
  TooltipCell,
} from '@/components/Table/components/Cell'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { numberWithCommas } from '@/utils/stringHelper'
import { filterRange } from '@/utils/tableHelper'
import { Box, Flex, Input, Text, Tooltip, VStack } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { useMemo, useState, useEffect } from 'react'
import { CiCircleAlert } from 'react-icons/ci'
import NumberFormat from 'react-number-format'

const columnHelper = createColumnHelper()

export const ACCESSOR_TO_HEADER = {
  ref: 'Ref',
  subSystemIndex: 'Sub-System',
  modelNumber: 'Model',
  description: 'Description',
  masterDescription: 'Master Description',
  quantity: 'Qty',
  currency: 'Currency',
  manualUnitCost: 'Unit Cost (CUR)',
  masterUnitCost: 'Master Unit Cost (CUR)',
  totalRequestedQuantity: 'Total PR Requested Quantity',
  totalRequestedCost: 'Total PR Requested Cost',
  newRequestedQuantity: 'New PR Requested Quantity',
  newRequestedCost: 'New PR Requested Cost',
}

export const useGCMaterialProjectExecutionColumns = ({
  currencyOptions,
  subSystemFilter,
  removeGCMaterial,
  getNewRequestedCostHKD,
  adminMode = false,
}) => {
  return useMemo(() => {
    const columns = [
      columnHelper.group({
        header: ' ',
        columns: [
          columnHelper.display({
            id: 'action',
            size: 10,
            header: () => <span>Action</span>,
            cell: ({ row: { index, original }, table }) => {
              const isDisabled = table.options.meta?.disabled
              const manualCreated = original?.manualCreated
              if (isDisabled || !manualCreated) return <></>

              const originalIndex = original?.originalIndex

              return (
                <DeleteButton
                  onClick={() => {
                    if (originalIndex !== undefined) {
                      removeGCMaterial(originalIndex)
                    } else {
                      console.error('Could not find originalIndex for delete at filtered index', index)
                    }
                  }}
                />
              )
            },
          }),
          columnHelper.display({
            id: 'ref',
            size: 50,
            enableHiding: false,
            enableColumnFilter: false,
            header: ACCESSOR_TO_HEADER.ref,
            headerColor: 'black',
            cell: ({ row: { index, original } }) => {
              const isApprovedByPM = !!original?.approvedByPM

              if (isApprovedByPM) {
                return <span>{index + 1}</span>
              }

              return (
                <Tooltip label="Pending PM approval">
                  <span style={{ fontWeight: 700 }}>{index + 1}</span>
                </Tooltip>
              )
            },
          }),
        ],
      }),
      columnHelper.group({
        header: 'GC Material List',
        columns: [
          columnHelper.accessor('subSystemIndex', {
            header: ACCESSOR_TO_HEADER.subSystemIndex,
            enableColumnFilter: false,
            cell: (cell) => {
              const value = cell.getValue()
              return <TooltipCell text={`1.${parseInt(value) + 1}`} />
            },
          }),
          columnHelper.accessor('modelNumber', {
            header: ACCESSOR_TO_HEADER.modelNumber,
            cell: (cell) => {
              const manualCreated = cell?.row?.original?.manualCreated
              if (manualCreated) {
                const currentRowIndex = cell?.row?.index
                const tableData = cell.table.options.data
                const currentModelNumber = cell.getValue()

                const isDuplicate =
                  currentModelNumber !== '' &&
                  tableData.some((row, idx) => idx !== currentRowIndex && row.modelNumber === currentModelNumber)

                return <InputCell {...cell} error={isDuplicate ? 'Duplicate Sub-System + Model Number' : ''} />
              }

              const value = cell.getValue()
              return <TooltipCell text={value} />
            },
          }),
          columnHelper.accessor('description', {
            header: ACCESSOR_TO_HEADER.description,
            filterFn: (row, columnId, filterValue) => {
              const rowDescription = row.getValue(columnId)
              return rowDescription.toLowerCase().includes(filterValue.toLowerCase())
            },
            cell: (cell) => {
              const manualCreated = cell?.row?.original?.manualCreated
              if (manualCreated) return <InputCell {...cell} />

              const value = cell.getValue()
              return <TooltipCell text={value} />
            },
          }),
          columnHelper.accessor('masterDescription', {
            header: ACCESSOR_TO_HEADER.masterDescription,
            filterFn: (row, columnId, filterValue) => {
              const rowDescription = row.getValue(columnId)
              return rowDescription.toLowerCase().includes(filterValue.toLowerCase())
            },
            cell: (cell) => {
              const masterDetail = cell?.row?.original?.detail
              let masterDescription = masterDetail?.description

              //check if discontinued

              let highLightColor = ''
              let highLightText = ''
              if (masterDetail?.discontinued) {
                // handle discontinued case if needed
                highLightColor = 'orange'
                highLightText = 'Discontinued'
              } else if (masterDetail?.status === 'inactive') {
                highLightColor = 'red'
                highLightText = 'Inactive'
                masterDescription = '(-)'
              }
              return (
                <TooltipCell
                  text={masterDescription && masterDescription !== '' ? masterDescription : '(-)'}
                  color={highLightColor}
                  tooltip={highLightText}
                />
              )
            },
          }),
          columnHelper.accessor('quantity', {
            header: ACCESSOR_TO_HEADER.quantity,
            cell: (cell) => {
              const manualCreated = cell?.row?.original?.manualCreated
              if (manualCreated) return <NumberInputCell {...cell} />

              const value = cell.getValue()
              return <TooltipCell text={value} />
            },
            filterFn: filterRange,
            meta: {
              filterVariant: FilterType.RANGE,
            },
          }),

          columnHelper.accessor('manualUnitCost', {
            header: ACCESSOR_TO_HEADER.manualUnitCost,
            filterFn: filterRange,
            meta: {
              filterVariant: FilterType.RANGE,
            },
            cell: (cell) => {
              const manualCreated = cell?.row?.original?.manualCreated
              if (manualCreated) return <NumberInputCell {...cell} />

              const value = cell.getValue()
              return <TooltipCell text={`$${numberWithCommas(value)}`} />
            },
          }),
          columnHelper.accessor('currency', {
            header: ACCESSOR_TO_HEADER.currency,
            cell: (cell) => {
              const manualCreated = cell?.row?.original?.manualCreated
              const hasMappedData = cell?.row?.original?.detail
              if (manualCreated && !hasMappedData) return <SelectCell {...cell} options={currencyOptions} />

              const masterCurrency = cell?.row?.original?.detail?.currency
              const rowCurrency = cell.getValue()
              return (
                <TooltipCell text={masterCurrency && masterCurrency !== '' ? masterCurrency : (rowCurrency ?? '(-)')} />
              )
            },
          }),
          columnHelper.accessor('masterUnitCost', {
            header: ACCESSOR_TO_HEADER.masterUnitCost,
            filterFn: filterRange,
            meta: {
              filterVariant: FilterType.RANGE,
            },
            cell: (cell) => {
              const masterDetail = cell?.row?.original?.detail
              const allDetails = cell?.row?.original?.allDetails
              const masterUnitCost = masterDetail?.unitCost ?? 0

              if (masterDetail?.status === 'inactive') {
                return <TooltipCell text={'(-)'} color={'red'} tooltip={'Inactive'} />
              }
              return (
                <Flex alignItems={'center'} gap="1" justifyContent={'center'}>
                  {Array.isArray(allDetails) && allDetails.length > 1 && (
                    <Tooltip
                      label={
                        <VStack alignItems={'start'}>
                          {allDetails.map((detail, detailIndex) => {
                            const vendorName = detail?.vendor !== '' ? detail?.vendor : '(Vendor)'
                            return (
                              <Text key={`${detail.SEN}-${detailIndex}`}>
                                {vendorName} - {detail.SEN} - {detail.currency} {numberWithCommas(detail.unitCost)}
                              </Text>
                            )
                          })}
                        </VStack>
                      }
                      closeOnScroll
                    >
                      <Box>
                        <CiCircleAlert size={'20'} />
                      </Box>
                    </Tooltip>
                  )}
                  <TooltipCell text={`$${numberWithCommas(masterUnitCost)}`} />
                </Flex>
              )
            },
          }),
        ],
      }),
      columnHelper.group({
        header: 'Total PR Requested',
        columns: [
          columnHelper.accessor('totalRequestedQuantity', {
            header: 'Qty',
            backgroundColor: 'gray.100',
            enableColumnFilter: false,
            cell: adminMode ? (cell) => <NumberInputCell {...cell} forceEnable decimalScale={2} /> : RawTextCell,
          }),
          columnHelper.accessor('totalRequestedCost', {
            header: 'Cost (HKD)',
            backgroundColor: 'gray.100',
            enableColumnFilter: false,
            cell: adminMode
              ? (cell) => <NumberInputCell {...cell} forceEnable decimalScale={2} pricePrefix="$" />
              : PriceCell,
          }),
        ],
      }),
    ]

    if (subSystemFilter !== null) {
      columns.push(
        columnHelper.group({
          header: 'New PR Requested',
          columns: [
            columnHelper.accessor('newRequestedQuantity', {
              header: 'Qty',
              enableColumnFilter: false,
              cell: (cell) => <NumberInputCell {...cell} decimalScale={2} />,
            }),
            columnHelper.display({
              id: 'newRequestedCost',
              header: 'Cost (HKD)',
              cell: ({ row: { original } }) => {
                const newRequestedCost = getNewRequestedCostHKD(original)

                return <span>${numberWithCommas(newRequestedCost)}</span>
              },
            }),
          ],
        })
      )
    }

    return columns
  }, [currencyOptions, subSystemFilter, removeGCMaterial, getNewRequestedCostHKD, adminMode])
}
